import type { SeedBucket } from './seed-buckets.js'

export type { SeedBucket } from './seed-buckets.js'

/** Coach highlight band for a topic within a seed bucket template. */
export type SeedHighlight = 'green' | 'yellow' | 'red'

type SeededBucket = Exclude<SeedBucket, 'knows_nothing'>

export type SeedTopicHighlights = {
  green: readonly string[]
  yellow: readonly string[]
  red: readonly string[]
}

/** Target mastery at 100% (green) for Easy / Intermediate / Advanced. */
export const BUCKET_TARGET_MASTERY: Record<SeededBucket, number> = {
  beginner: 20,
  intermediate: 50,
  advanced: 70,
}

/** Multipliers applied to bucket target mastery (tunable). Green = 100%. */
export const HIGHLIGHT_MULTIPLIERS: Record<SeedHighlight, number> = {
  green: 1,
  yellow: 0.66,
  red: 0.33,
}

/**
 * Per-bucket topic highlight lists (Mike 2026-05-29, updated for subtopics.json
 * slugs and again for the 2026-06 taxonomy convergence: `prefix-sum` →
 * `range-queries`; `fenwick-tree` + `segment-tree` → `advanced-range-queries`).
 * Topics not listed → mastery 0. Subtopics inherit parent topic highlight.
 *
 * Slugs are stored normalized (hyphenated). `normalizeTopicSlug` accepts DB slugs
 * with spaces or legacy hyphen forms from the pre-subtopics.json catalog.
 */
export const SEED_TOPIC_HIGHLIGHTS: Record<SeededBucket, SeedTopicHighlights> =
  {
    beginner: {
      green: [
        'dynamic-arrays',
        'string-manipulation',
        'two-pointers',
        'grids-and-matrices',
        'binary-search',
        'sets',
        'maps',
        'stacks',
        'queues',
        'sorting',
      ],
      yellow: ['recursion', 'linked-lists', 'doubly-linked-lists'],
      red: ['graphs', 'backtracking'],
    },
    intermediate: {
      green: [
        'dynamic-arrays',
        'string-manipulation',
        'two-pointers',
        'grids-and-matrices',
        'binary-search',
        'sets',
        'maps',
        'stacks',
        'queues',
        'recursion',
        'sorting',
        'linked-lists',
        'doubly-linked-lists',
        'trees',
        'graphs',
        'heaps',
        'backtracking',
        'range-queries',
        'tries',
      ],
      yellow: ['sliding-windows', 'topological-sort'],
      red: ['dynamic-programming', 'greedy', 'math'],
    },
    advanced: {
      green: [
        'dynamic-arrays',
        'string-manipulation',
        'two-pointers',
        'grids-and-matrices',
        'binary-search',
        'sets',
        'maps',
        'stacks',
        'queues',
        'recursion',
        'sorting',
        'linked-lists',
        'doubly-linked-lists',
        'trees',
        'graphs',
        'heaps',
        'sliding-windows',
        'backtracking',
        'topological-sort',
        'range-queries',
        'dynamic-programming',
        'greedy',
        'tries',
      ],
      yellow: [
        'union-find',
        'advanced-graphs',
        'math',
        // Merged from the former `fenwick-tree` + `segment-tree` entries
        // (both were yellow here, so the merge is lossless).
        'advanced-range-queries',
      ],
      red: ['bit-manipulation'],
    },
  }

/** Subtopic highlight overrides — keys `{topicSlug}__{subtopicSlug}`. Placeholder. */
export const SUBTOPIC_SEED_HIGHLIGHT_OVERRIDE: Record<string, SeedHighlight> =
  {}

export function normalizeTopicSlug(slug: string): string {
  return slug
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, '-')
}

export function getHighlightMasteryValues(bucket: SeededBucket): {
  target: number
  green: number
  yellow: number
  red: number
} {
  const target = BUCKET_TARGET_MASTERY[bucket]
  return {
    target,
    green: Math.round(target * HIGHLIGHT_MULTIPLIERS.green),
    yellow: Math.round(target * HIGHLIGHT_MULTIPLIERS.yellow),
    red: Math.round(target * HIGHLIGHT_MULTIPLIERS.red),
  }
}

export function formatHighlightSummary(bucket: SeededBucket): string {
  const v = getHighlightMasteryValues(bucket)
  return `Target ${v.target} · G ${v.green} · Y ${v.yellow} · R ${v.red}`
}

function subtopicOverrideKey(topicSlug: string, subtopicSlug: string): string {
  return `${normalizeTopicSlug(topicSlug)}__${normalizeTopicSlug(subtopicSlug)}`
}

export function resolveTopicSeedHighlight(
  bucket: SeedBucket,
  topicSlug: string,
): SeedHighlight | null {
  if (bucket === 'knows_nothing') {
    return null
  }

  const normalizedTopicSlug = normalizeTopicSlug(topicSlug)
  const highlights = SEED_TOPIC_HIGHLIGHTS[bucket]
  if (highlights.green.includes(normalizedTopicSlug)) return 'green'
  if (highlights.yellow.includes(normalizedTopicSlug)) return 'yellow'
  if (highlights.red.includes(normalizedTopicSlug)) return 'red'
  return null
}

export function resolveSeedHighlight(
  bucket: SeedBucket,
  topicSlug: string,
  subtopicSlug: string,
): SeedHighlight | null {
  if (bucket === 'knows_nothing') {
    return null
  }

  const overrideKey = subtopicOverrideKey(topicSlug, subtopicSlug)
  const subtopicOverride = SUBTOPIC_SEED_HIGHLIGHT_OVERRIDE[overrideKey]
  if (subtopicOverride !== undefined) {
    return subtopicOverride
  }

  return resolveTopicSeedHighlight(bucket, topicSlug)
}

export function getSeedMasteryForSubtopic(
  bucket: SeedBucket,
  topicSlug: string,
  subtopicSlug: string,
): number {
  if (bucket === 'knows_nothing') {
    return 0
  }

  const highlight = resolveSeedHighlight(bucket, topicSlug, subtopicSlug)
  if (highlight === null) {
    return 0
  }

  const target = BUCKET_TARGET_MASTERY[bucket]
  return Math.round(target * HIGHLIGHT_MULTIPLIERS[highlight])
}
